<template>
	<div class="file-system-level" @click.stop>
		<input type="file" ref="fileInput" multiple class="file-input-hidden" @change="handleFiles" />
		<div v-if="filteredItems.length === 0" class="file-empty-state">
			<span class="file-empty-icon">
				<portal-icon name="file-text" :size="20"></portal-icon>
			</span>
			<span>No uploaded file attachments.</span>
		</div>
		<template v-for="item in filteredItems">
			<div
				:key="'main' + item.id"
				class="file-table-row file-table-grid"
				:class="{ 'is-deleted': item.isDeleted || parentDeleted }"
				@contextmenu.stop.prevent="showContextMenu($event, item)"
			>
				<div
					class="file-name-cell"
					:style="{ paddingLeft: `${12 + depth * 18}px` }"
					@dragover="handleDragOver"
					@drop="handleDrop"
					@dragstart="handleDragStart"
					@dragenter="handleDragEnter"
					@dragleave="handleDragLeave"
					:draggable="!readOnly"
					:data-drop-enabled="canUpload ? 'true' : 'false'"
					:data-asset-type="item.assetType"
					:data-asset-id="item.id"
					:id="item.id"
				>
					<button
						v-if="item.assetType === 'Folder'"
						type="button"
						class="file-tree-toggle"
						:aria-expanded="isExpanded(item) ? 'true' : 'false'"
						@click.stop="toggle(item)"
					>
						<portal-icon :name="isExpanded(item) ? 'chevron-down' : 'chevron-right'" :size="15"></portal-icon>
					</button>
					<span
						v-else
						class="file-tree-spacer"
						:class="{ 'is-root-file': depth === 0 }"
					></span>

					<button
						v-if="item.assetType === 'Folder'"
						type="button"
						class="file-type-icon is-folder"
						@click.stop="toggle(item)"
					>
						<portal-icon :name="isExpanded(item) ? 'folder-open' : 'folder'" :size="18"></portal-icon>
					</button>
					<button v-else type="button" class="file-type-icon" :aria-label="item.name" @click.stop="activateItem(item)">
						<img
							v-if="item.fileIconUrl"
							:src="item.fileIconUrl"
							alt=""
							draggable="false"
						/>
						<portal-icon v-else name="file-text" :size="17"></portal-icon>
					</button>

					<div
						class="file-name-text"
						:contenteditable="item.isDeleted || parentDeleted || isDragging || readOnly ? 'false' : 'true'"
						:class="{ 'deleted-item': item.isDeleted || parentDeleted }"
						:title="item.name"
						@mousedown="handleMouseDown"
						@input="handleInput($event, item)"
						@keydown="preventLineBreak($event)"
						@blur="saveName(item)"
						@click.stop="readOnly ? activateItem(item) : null"
						v-tippy
						:content="item.notes"
					>
						{{ item.name }}
					</div>
				</div>

				<div
					class="file-meta file-col-modified"
					v-tippy
					:content="DateTime.fromISO(item.updated).toLocaleString(DateTime.DATETIME_SHORT)"
				>
					{{ $formatters.formatForTimeAgo(item.updated) }}
				</div>
				<div
					class="file-meta file-col-size"
					v-tippy
					:content="item.size ? $formatters.formatBytes(item.size) : null"
				>
					{{ item.size ? $formatters.formatBytes(item.size) : '—' }}
				</div>
				<div class="file-meta file-col-version">
					{{ item.versions && item.versions.length ? item.versions.length : '—' }}
				</div>
				<div class="file-row-actions">
					<button
						type="button"
						class="file-more-action"
						:aria-label="item.name"
						aria-haspopup="menu"
						@click.stop="showContextMenu($event, item, true)"
					>
						<portal-icon name="ellipsis" :size="18"></portal-icon>
					</button>
				</div>
			</div>
			<file-system
				:key="'children' + item.id"
				:ref="'children-' + item.id"
				v-if="item.assetType === 'Folder' && isExpanded(item)"
				:parent-folder-id="item.id"
				:show-deleted="showDeleted"
				:parent-deleted="item.isDeleted || parentDeleted"
				:depth="depth + 1"
				:is-dragging="isDragging"
				:sort-by="sortBy"
				:sort-dir="sortDir"
				:read-only="readOnly"
				:allow-upload="allowUpload"
			></file-system>
		</template>
		<div
			v-if="depth === 0"
			class="file-root-drop-target"
			@dragover="handleDragOver"
			@drop="handleDrop"
			@dragstart="handleDragStart"
			@dragenter="handleDragEnter"
			@dragleave="handleDragLeave"
			@click.stop
			@contextmenu.stop.prevent="showContextMenu($event, null)"
			:data-drop-enabled="canUpload ? 'true' : 'false'"
			data-asset-type="Folder"
			:data-asset-id="parentFolderId"
		></div>

		<v-menu
			v-model="contextMenu.visible"
			:position-x="contextMenu.x"
			:position-y="contextMenu.y"
			absolute
			offset-y
			content-class="file-context-menu-surface"
		>
			<div class="file-context-menu" role="menu">
				<button
					type="button"
					class="file-context-menu-item"
					@click="openFile"
					v-if="contextActionItem && contextActionItem.assetType === 'File' && isImageType"
				>
					<portal-icon name="eye" :size="16"></portal-icon>
					<span>Open</span>
				</button>
				<button
					type="button"
					class="file-context-menu-item"
					@click="downloadFile"
					v-if="contextActionItem && contextActionItem.assetType === 'File'"
				>
					<portal-icon name="download" :size="16"></portal-icon>
					<span>Download</span>
				</button>
				<div
					v-if="contextActionItem && contextActionItem.assetType === 'File'"
					class="file-context-menu-divider"
				></div>
				<button
					type="button"
					class="file-context-menu-item"
					@click="handleUploadFile"
					v-if="!contextMenu.moreMenu && canUpload && (!contextActionItem || !contextActionItem.isDeleted)"
				>
					<portal-icon name="upload" :size="16"></portal-icon>
					<span>Upload file</span>
				</button>
				<button
					type="button"
					class="file-context-menu-item"
					@click="handleNewFolder"
					v-if="!contextMenu.moreMenu && !readOnly && (!contextActionItem || !contextActionItem.isDeleted)"
				>
					<portal-icon name="folder-plus" :size="16"></portal-icon>
					<span>New folder</span>
				</button>
				<div
					v-if="!readOnly && contextActionItem"
					class="file-context-menu-divider"
				></div>
				<button
					type="button"
					class="file-context-menu-item is-danger"
					@click="deleteItem"
					v-if="!readOnly && contextActionItem"
				>
					<portal-icon name="trash-2" :size="16"></portal-icon>
					<span>Delete</span>
				</button>
			</div>
		</v-menu>
		<light-box v-if="lightBoxUrl" :url="lightBoxUrl" :file-name="lightBoxFileName" @close="closeLightBox()"></light-box>
	</div>
</template>

<script>
	import FileSystemService from '@/modules/files/FileSystemService';
	import ConfirmModal from '@/components/ConfirmModal';
	import { DateTime } from 'luxon';
	import axios from 'axios';
	import LightBox from '@/modules/files/LightBox';

	export default {
		name: 'FileSystem',

		props: {
			parentFolderId: {
				type: String,
				default: null,
			},
      showDeleted: {
        type: Boolean,
        default: false,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
			allowUpload: {
				type: Boolean,
				default: false,
			},
			parentDeleted: {
				type: Boolean,
				default: false,
			},
			isDragging: {
				type: Boolean,
				default: false,
			},
			depth: {
				type: Number,
				default: 0,
			},
			sortBy: {
				type: String,
				default: 'name'
			},
			sortDir: {
				type: String,
				default: 'desc',
			},
		},

		// Recursive component registration
		components: {
			LightBox,
			FileSystem: () => import('./FileSystem.vue'),
		},

		data() {
			return {
				DateTime: DateTime,
				fileSystemService: new FileSystemService(),
				items: [],
				isLoading: false,
				// Tracks expanded folders in the nested view (using item.id as key)
				expandedFolders: {},
				contextActionItem: null,
				uploadFileFolderId: null,
				contextMenu: {
          moreMenu: false,
					visible: false,
					x: 0,
					y: 0,
					// folder: null, // If needed later to track right-clicked folder
				},
				lightBoxUrl: null,
				lightBoxFileName: null,
			};
		},

		mounted() {
			this.fetchFolderContents();
			// Hide context menu on any click outside it
			document.addEventListener('click', this.hideContextMenu);
			this.$store.state.eventBus.$on('file-system-move-event', this.handleMoveEvent);
		},

		beforeDestroy() {
			document.removeEventListener('click', this.hideContextMenu);
			this.$store.state.eventBus.$off('file-system-move-event', this.handleMoveEvent);
		},

		methods: {
			activateItem: function(fileAsset) {
				this.contextActionItem = fileAsset;
				this.isImageType ? this.openFile() : this.downloadFile();
			},

			toggleShowDeleted: function() {
				this.$store.state.eventBus.$emit('file-system-toggle-deleted');
				this.hideContextMenu();
			},

			closeLightBox: function() {
				this.lightBoxUrl = null;
				this.lightBoxFileName = null;
			},

			openFile: function() {
				let fileAsset = this.contextActionItem;
				this.fileSystemService
					.getSignedUrl(fileAsset.id, false)
					.then((res) => {
						console.log(res.data);
						this.lightBoxUrl = res.data;
						this.lightBoxFileName = fileAsset.name;
					})
					.finally(() => this.hideContextMenu());
			},

			downloadFile: function() {
				let fileAsset = this.contextActionItem;
				this.fileSystemService
					.getSignedUrl(fileAsset.id, true)
					.then((res) => {
						let signedUrl = res.data;

						axios({
							url: signedUrl, //your url
							method: 'GET',
							responseType: 'blob', // important
						}).then((response) => {
							const url = window.URL.createObjectURL(new Blob([response.data]));
							const link = document.createElement('a');
							link.href = url;
							link.setAttribute('download', fileAsset.name);
							document.body.appendChild(link);
							link.click();
						});
					})
					.finally(() => this.hideContextMenu());
			},

			handleMoveEvent: function(fileAsset) {
				let ix = this.items.findIndex((i) => i.id === fileAsset.id);

				if (fileAsset.parentFolderId === this.parentFolderId) {
					if (ix === -1) {
						this.items.push(fileAsset);
					}
				} else if (ix > -1) {
					this.items.splice(ix, 1);
				}
			},

			fetchFolderContents() {
				this.isLoading = true;
				this.fileSystemService
					.list(this.parentFolderId)
					.then((res) => {
						this.items.splice(0);
						this.items.push(...res.data);
					})
					.finally(() => (this.isLoading = false));
			},

      sortItems(itemArray) {
        itemArray.sort((a, b) => {
          let valA = a[this.sortBy];
          let valB = b[this.sortBy];

          // Handle null/undefined values
          if (valA == null && valB == null) return 0;
          if (valA == null) return this.sortDir === 'asc' ? -1 : 1;
          if (valB == null) return this.sortDir === 'asc' ? 1 : -1;

          // If both are strings, convert to lower case for case-insensitive comparison.
          if (typeof valA === 'string' && typeof valB === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
          }

          if (valA < valB) return this.sortDir === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      },

			handleMouseDown(event) {
				if (event.button === 2) {
					event.preventDefault();
				}
			},

			handleInput(event, item) {
				item.temporaryName = event.target.innerText;
			},

			preventLineBreak(e) {
				const forbiddenKeys = ['Enter', 'Tab'];
				if (forbiddenKeys.includes(e.key)) {
					e.preventDefault();
				}
			},

			// Helper function to check if drag is enabled for this folder
			isDropEnabled(event) {
				return event.currentTarget.dataset.dropEnabled === 'true';
			},

			handleDragStart(e) {
				console.log('starting to drag ' + e.target.id);
				e.dataTransfer.setData('text/plain', e.target.id);
			},

			handleDragEnter(e) {
				if (!this.isDropEnabled(e)) return;
				e.preventDefault();
				e.currentTarget.classList.add('highlight');
			},

			handleDragLeave(e) {
				if (!this.isDropEnabled(e)) return;
				e.currentTarget.classList.remove('highlight');
			},

			handleDragOver(e) {
				if (!this.isDropEnabled(e)) return;
				e.preventDefault();
				e.dataTransfer.dropEffect = 'copy'; // Indicate that files will be copied
			},

			handleDrop(e) {
				if (!this.isDropEnabled(e)) return;
				e.preventDefault();
				e.currentTarget.classList.remove('highlight');
				let sourceId = e.dataTransfer.getData('text/plain');

				if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
					let uploadFolderId = e.currentTarget.dataset.assetId;
					this.uploadFiles(uploadFolderId, e.dataTransfer.files);
				} else if (!this.readOnly && sourceId && e.currentTarget.dataset.assetType === 'Folder') {
					let targetId = e.currentTarget.dataset.assetId;

					if (targetId !== sourceId) {
						this.fileSystemService.move(sourceId, targetId).then((res) => {
							this.$store.state.eventBus.$emit('file-system-move-event', res.data);
						});
					}
				}else if(!this.readOnly && sourceId && e.currentTarget.dataset.assetType === 'File'){
					let targetId = this.parentFolderId;
					if (targetId !== sourceId) {
						this.fileSystemService.move(sourceId, targetId).then((res) => {
							this.$store.state.eventBus.$emit('file-system-move-event', res.data);
						});
					}
				}
			},

			handleUploadFile() {
				this.uploadFileFolderId =
					this.contextActionItem && this.contextActionItem.assetType === 'Folder'
						? this.contextActionItem.id
						: this.parentFolderId;
				this.hideContextMenu();
				this.$refs.fileInput.click();
			},

			handleFiles(event) {
				let files = event.target.files;
				let uploadFileFolderId = this.uploadFileFolderId;
				this.uploadFiles(uploadFileFolderId, files);
				this.$refs.fileInput.value = '';
			},

			async uploadFiles(uploadFileFolderId, files) {
				let ourFiles = [];

        for(let i=0; i < files.length; i++){
          ourFiles.push(files[i]);
        }

				this.$store.commit('startLoading');
				try {
					let uploadedFiles = [];
					for (let i = 0; i < ourFiles.length; i++) {
						let result = await this.fileSystemService.upload(uploadFileFolderId, ourFiles[i]);
						uploadedFiles.push(result.data);
					}

					if (uploadFileFolderId === this.parentFolderId) {
						uploadedFiles.forEach((u) => {
							let ix = this.items.findIndex((i) => i.id === u.id);
							if (ix > -1) {
								this.items.splice(ix, 1, u);
							} else {
								this.items.push(u);
							}
						});
					} else {
						let item = this.items.find((i) => i.id === uploadFileFolderId);
						if (this.isExpanded(item)) {
							uploadedFiles.forEach((f) => this.$refs['children-' + item.id][0].addItem(f));
						} else {
							this.toggle(item);
						}
					}
				} catch (err) {
					console.log(err);
				} finally {
					this.$store.commit('stopLoading');
				}
			},

			saveName(item) {
				if (!item.temporaryName) {
					return;
				}
				this.fileSystemService
					.rename(item.id, item.temporaryName)
					.then((res) => {
						let ix = this.items.findIndex((i) => i.id === item.id);
						if (ix > -1) {
							this.items.splice(ix, 1, res.data);
						}
					})
					.catch((err) => {
						console.error('Failed to save name', err);
					});
			},

			addFolder(parentId) {
				this.fileSystemService
					.createDirectory(parentId, 'New Folder')
					.then((res) => {
						if (parentId === this.parentFolderId) {
							this.items.push(res.data);
						} else {
							let item = this.items.find((i) => i.id === parentId);
							if (this.isExpanded(item)) {
								this.$refs['children-' + item.id][0].addItem(res.data);
							} else {
								this.toggle(item);
							}
						}
					})
					.finally(() => this.hideContextMenu());
			},

			unDeleteItem() {
				let item = this.contextActionItem;
				this.fileSystemService
					.unDelete(item.id)
					.then((res) => {
						let ix = this.items.findIndex((i) => i.id === item.id);
						if (ix > -1) {
							this.items.splice(ix, 1, res.data);
						}
					})
					.finally(() => this.hideContextMenu());
			},

			deleteItem() {
				let item = this.contextActionItem;
				this.hideContextMenu();

				let binding = {
					headingText: 'Confirm',
					bodyText: null,
				};

				if (item.assetType === 'Folder') {
					binding.bodyText = `Are you sure you want to delete the folder <strong>${item.name}</strong> and all of it's contents?`;
				} else {
					binding.bodyText = `Are you sure you want to delete the file <strong>${item.name}</strong>?`;
				}

				this.$store.state.globalModalController.openModal(ConfirmModal, binding).then((res) => {
					if (res) {
						this.fileSystemService.delete(item.id).then((res) => {
							let ix = this.items.findIndex((i) => i.id === item.id);
							if (ix > -1) {
								this.items.splice(ix, 1, res.data);
							}
						});
					}
				});
			},

			addItem(item) {
				let ix = this.items.findIndex((i) => i.id === item.id);
				if (ix > -1) {
					this.items.splice(ix, 1, item);
				} else {
					this.items.push(item);
				}
			},

			toggle(item) {
				if (item.assetType !== 'Folder') return;
				this.$set(this.expandedFolders, item.id, !this.expandedFolders[item.id]);
			},

			isExpanded(item) {
				return !!this.expandedFolders[item.id];
			},

			// Show the context menu at the click position
			showContextMenu(event, item, moreMenu = false) {
				if (this.parentDeleted) {
					return;
				}

				const safeBottom = window.innerWidth <= 599 ? 82 : 12;
				const estimatedMenuHeight = moreMenu ? 160 : 190;
				const maxMenuTop = window.innerHeight - safeBottom - estimatedMenuHeight;

				if (moreMenu && event.currentTarget) {
					const rect = event.currentTarget.getBoundingClientRect();
					this.contextMenu.x = Math.max(12, Math.min(rect.right - 190, window.innerWidth - 202));
					this.contextMenu.y = Math.max(12, Math.min(rect.bottom + 4, maxMenuTop));
				} else {
					this.contextMenu.x = Math.max(12, Math.min(event.clientX, window.innerWidth - 202));
					this.contextMenu.y = Math.max(12, Math.min(event.clientY, maxMenuTop));
				}
				this.contextMenu.visible = true;
				this.contextMenu.moreMenu = moreMenu;
				this.contextActionItem = item;
			},

			// Hide the context menu
			hideContextMenu() {
				this.contextMenu.visible = false;
				this.contextActionItem = null;
			},

			// Handle clicking on "New Folder"
			handleNewFolder() {
				let parentId = this.parentFolderId;
				if (this.contextActionItem?.assetType === 'Folder') {
					parentId = this.contextActionItem.id;
				}
				this.addFolder(parentId);
				this.hideContextMenu();
			},
		},

		computed: {
			canUpload: function() {
				return !this.readOnly || this.allowUpload;
			},
			isImageType: function() {
				let fileAsset = this.contextActionItem;
				if (fileAsset && fileAsset.assetType === 'File') {
					let name = fileAsset.name.toUpperCase();
					if (
						name.endsWith('JPG') ||
						name.endsWith('PNG') ||
						name.endsWith('GIF') ||
						name.endsWith('PDF') ||
						name.endsWith('SVG')
					) {
						return true;
					} else {
						return false;
					}
				} else {
					return false;
				}
			},

			filteredItems: function() {
				let items = [];
				if (this.showDeleted) {
					items.push(... this.items);
				} else {
					items.push(... this.items.filter((i) => !i.isDeleted));
				}
				this.sortItems(items);
				return items;
			},
		},
	};
</script>

<style scoped lang="scss">
	.file-system-level {
		min-width: 0;
	}

	.file-input-hidden {
		display: none;
	}

	.file-empty-state {
		min-height: 132px;
		display: grid;
		place-content: center;
		justify-items: center;
		gap: 9px;
		padding: 20px;
		color: var(--portal-muted);
		font-size: 12px;
	}

	.file-empty-icon {
		width: 38px;
		height: 38px;
		display: grid;
		place-items: center;
		border-radius: 11px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
	}

	.file-table-row {
		min-height: 52px;
		padding: 0 4px;
		border-bottom: 1px solid var(--portal-border);
		background: var(--portal-table-row-background, var(--portal-surface));
		color: var(--portal-table-row-text, var(--portal-text));
		transition: background-color 120ms ease;
	}

	.file-table-row:hover {
		background: var(--portal-table-row-hover, var(--v-gray_10-base));
	}

	.file-table-row.is-deleted {
		opacity: 0.62;
	}

	.file-name-cell {
		min-width: 0;
		min-height: 51px;
		display: flex;
		align-items: center;
		gap: 7px;
		padding-right: 10px;
	}

	.file-tree-toggle,
	.file-type-icon,
	.file-more-action {
		flex: 0 0 auto;
		display: grid;
		place-items: center;
		padding: 0;
		border: 0;
		background: transparent;
		color: var(--portal-table-row-muted, var(--portal-muted));
	}

	.file-tree-toggle {
		width: 22px;
		height: 30px;
		border-radius: var(--brand-button-radius, 7px);
		cursor: pointer;
	}

	.file-tree-toggle:hover,
	.file-tree-toggle:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.file-tree-spacer {
		width: 22px;
		flex: 0 0 22px;
	}

	.file-tree-spacer.is-root-file {
		width: 0;
		flex-basis: 0;
	}

	.file-type-icon {
		width: 30px;
		height: 30px;
		border-radius: 9px;
		background: var(--v-gray_20-base);
	}

	button.file-type-icon {
		cursor: pointer;
	}

	.file-type-icon.is-folder {
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.file-type-icon img {
		max-width: 18px;
		max-height: 18px;
		display: block;
	}

	.file-name-text {
		min-width: 0;
		flex: 1;
		overflow: hidden;
		color: var(--portal-table-row-text, var(--portal-text));
		font-size: 13px;
		font-weight: 620;
		line-height: 1.35;
		text-align: left;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.file-name-text[contenteditable='false'] {
		cursor: pointer;
	}

	div[contenteditable='true']:focus {
		padding: 5px 7px;
		border-radius: var(--brand-field-radius, 7px);
		outline: 2px solid color-mix(in srgb, var(--v-primary-base) 28%, transparent);
		background: var(--portal-surface);
		overflow: visible;
		text-overflow: clip;
	}

	.deleted-item {
		color: var(--portal-table-row-muted, var(--portal-muted));
		text-decoration: line-through;
	}

	.file-meta {
		min-width: 0;
		padding: 0 10px;
		overflow: hidden;
		color: var(--portal-table-row-muted, var(--portal-muted));
		font-size: 12px;
		text-align: left;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.file-row-actions {
		display: grid;
		place-items: center;
	}

	.file-more-action {
		width: 32px;
		height: 32px;
		border-radius: var(--brand-button-radius, 9px);
		cursor: pointer;
	}

	.file-more-action:hover,
	.file-more-action:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.file-root-drop-target {
		min-height: 12px;
	}

	.highlight {
		border-radius: var(--brand-surface-radius, 9px);
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--v-primary-base) 30%, transparent);
	}

	@media (prefers-reduced-motion: reduce) {
		.file-table-row {
			transition: none;
		}
	}
</style>

<style lang="scss">
	.file-context-menu-surface {
		min-width: 190px !important;
		padding: 6px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 12px) !important;
		background: var(--portal-surface);
		box-shadow: 0 14px 38px rgba(17, 24, 39, 0.16) !important;
		overflow: hidden;
	}

	.file-context-menu {
		display: grid;
		gap: 2px;
	}

	.file-context-menu-item {
		width: 100%;
		min-height: 38px;
		display: flex;
		align-items: center;
		gap: 9px;
		padding: 0 10px;
		border: 0;
		border-radius: var(--brand-button-radius, 8px);
		background: transparent;
		color: var(--portal-text);
		font: inherit;
		font-size: 12px;
		font-weight: 600;
		text-align: left;
		cursor: pointer;
	}

	.file-context-menu-item:hover,
	.file-context-menu-item:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
	}

	.file-context-menu-item.is-danger {
		color: var(--v-alert-base);
	}

	.file-context-menu-item.is-danger:hover,
	.file-context-menu-item.is-danger:focus-visible {
		background: var(--v-alert_10-base);
	}

	.file-context-menu-divider {
		height: 1px;
		margin: 4px 5px;
		background: var(--portal-border);
	}
</style>
